'use client';

import type { RenderProps } from '@musinsa/curator-shared-ts';
import { useEffect, useRef } from 'react';

export type UseCommonLayoutProps = {
  containers: RenderProps['containers'];
  options: RenderProps['options'];
  isPcEnvironment?: boolean;
};

export const useCommonLayout = (props: UseCommonLayoutProps) => {
  const { containers, options, isPcEnvironment = false } = props;

  const fabRef = useRef<HTMLElement | null>(null);
  const gnbRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const targetLayout = isPcEnvironment ? window.commonLayoutPc : window.commonLayout;

    const render = () => {
      fabRef.current = document.querySelector('#commonLayoutFab');
      gnbRef.current = document.querySelector('#commonLayoutGnb');

      const layout = isPcEnvironment ? window.commonLayoutPc : window.commonLayout;
      if (!fabRef.current || !gnbRef.current || !layout) {
        return;
      }

      try {
        layout.render?.({
          containers,
          options: isPcEnvironment ? { contentSize: 'max1440', ...options } : options,
        });
      } catch (error) {
        /**
         * NOTE: 아래의 오류가 발생하여 에러를 로깅합니다.
         * hook.js:608 NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
         */
        console.error({
          hook: 'useCommonLayout',
          error,
        });
      }
    };

    // NOTE: 이미 스크립트가 로드된 경우 즉시 렌더
    if (targetLayout) {
      render();
      return;
    }

    // NOTE: App Router에서 SSR hydration이 module script(render.js)보다 먼저 완료될 수 있습니다.
    // module script는 DOMContentLoaded 전에 실행이 보장되므로, 해당 이벤트를 대기합니다.
    const handler = () => render();
    document.addEventListener('DOMContentLoaded', handler, { once: true });
    return () => document.removeEventListener('DOMContentLoaded', handler);
  }, [containers, options, isPcEnvironment]);

  return null;
};

/**
 * useCommonLayout을 사용하면서, 공통 레이아웃을 제거하고 싶을 때 사용합니다.
 */
export const useRemoveCommonLayout = ({
  removeAppBar = false,
  removeFooter = false,
  appBarId = 'commonLayoutHeader',
  footerId = 'commonLayoutGnb',
}) => {
  useEffect(() => {
    if (removeAppBar) {
      const clAppBarEl = window.document.getElementById(appBarId);
      if (clAppBarEl) {
        clAppBarEl.remove();
      }
    }

    if (removeFooter) {
      const clFooterEl = window.document.getElementById(footerId);
      if (clFooterEl) {
        clFooterEl.remove();
      }
    }
  }, [appBarId, footerId, removeAppBar, removeFooter]);
};
